import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { MessageAPI } from '@/services/message'

export interface MessageItem {
  id: string
  name: string
  avatar: string
  content: string
  background: string
  roleId: string
  createdAt: number

}

type TabType = 1 | 2

interface TabState {
  items: MessageItem[]
  page: number
  pageSize: number
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  initialized: boolean
  scrollTop: number
  lastLoadedAt: number
  error: string | null // 🔧 新增：错误状态
}

const createInitialState = (): TabState => ({
  items: [],
  page: 1,
  pageSize: 20,
  hasMore: true,
  loading: false,
  loadingMore: false,
  initialized: false,
  scrollTop: 0,
  lastLoadedAt: 0,
  error: null, // 🔧 新增：初始化错误状态
})

export const useMessageStore = defineStore('message', () => {
  const activeTab = ref<TabType>(2) // 2: All, 1: Like
  const tabStates = reactive<Record<string, TabState>>({
    '1': createInitialState(),
    '2': createInitialState(),
  })

  const ensureTab = (tab: TabType) => tabStates[String(tab)]

  const setActiveTab = (tab: TabType) => {
    activeTab.value = tab
  }

  const getCurrentState = () => ensureTab(activeTab.value)

  const saveScrollPosition = (scrollTop: number, tab: TabType = activeTab.value) => {
    ensureTab(tab).scrollTop = scrollTop
  }

  const getScrollPosition = (tab: TabType = activeTab.value) => ensureTab(tab).scrollTop

  const shouldRefresh = (tab: TabType = activeTab.value) => !ensureTab(tab).initialized

  const fetchList = async (tab: TabType, page: number, pageSize: number) => {
    // 后端若不支持分页，会返回固定数据；前端仍可缓存与统一管理
    const res = await MessageAPI.getMessageList({ type: tab, page, pageSize,isSimple:true })
    return Array.isArray(res?.data) ? (res.data as MessageItem[]) : []
  }

  const refresh = async (tab: TabType = activeTab.value) => {
    const state = ensureTab(tab)
    if (state.loading) return
    state.loading = true
    state.error = null // 🔧 清除之前的错误
    try {
      state.page = 1
      const data = await fetchList(tab, state.page, state.pageSize)
      state.items = data || []
      state.hasMore = data?.length >= state.pageSize
      state.initialized = true
      state.lastLoadedAt = Date.now()
      state.error = null // 🔧 成功时清除错误
    } catch (error: any) {
      // 🔧 捕获错误并保存错误信息
      state.error = error?.message || 'Failed to load messages'
      console.error('Failed to refresh messages:', error)
    } finally {
      state.loading = false
    }
  }

  const loadMore = async (tab: TabType = activeTab.value) => {
    const state = ensureTab(tab)
    if (state.loadingMore || !state.hasMore) return
    state.loadingMore = true
    try {
      const nextPage = state.page + 1
      const data = await fetchList(tab, nextPage, state.pageSize)
      state.items = state.items.concat(data || [])
      state.page = nextPage
      state.hasMore = data?.length >= state.pageSize
      state.lastLoadedAt = Date.now()
    } finally {
      state.loadingMore = false
    }
  }

  const switchTab = async (tab: TabType) => {
    setActiveTab(tab)
    await refresh(tab)
  }

  const deleteById = (id: string, tab: TabType = activeTab.value) => {
    const state = ensureTab(tab)
    state.items = state.items.filter(i => i.id !== id)
  }

  return {
    activeTab,
    tabStates,
    ensureTab,
    getCurrentState,
    getScrollPosition,
    shouldRefresh,
    setActiveTab,
    saveScrollPosition,
    refresh,
    loadMore,
    switchTab,
    deleteById,
  }
})


